import { GetShopProductsResponse } from "@1games-ru/region-shared/dist/backend/requests/shop";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ShopMpProvider from "@/core/data-provider/data-mp/shop/shop-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import toast from "@/domains/toasts/helpers/toast";

export const QUERY_KEY_SHOP_GET_PRODUCTS = "QUERY_KEY_SHOP_GET_PRODUCTS";

export default function useGetProducts(isShow: boolean, shopId: number) {
  const { t, i18n } = useTranslation("shop");

  const query = useQuery<GetShopProductsResponse>({
    queryKey: [QUERY_KEY_SHOP_GET_PRODUCTS, shopId],
    enabled: isShow && !!shopId,
    retry: false,
    queryFn: async () => {
      if (!shopId) return null;
      const response = await ShopMpProvider.getProducts(shopId);

      if (response instanceof RpcMpProviderSuccessResponse) {
        return response.data;
      }

      const messageKey = response?.error?.message ?? "Unknown";
      const knownI18nKey = i18n.exists(`shop:errors.${messageKey}`);

      const error = new Error(messageKey) as Error & { knownI18nKey?: boolean };
      error.knownI18nKey = knownI18nKey;
      throw error;
    },
  });

  useEffect(() => {
    if (!query.isError) return;
    const error = query.error as Error & { knownI18nKey?: boolean };
    // eslint-disable-next-line no-console
    console.error(error);

    if (error.knownI18nKey) {
      toast.error(t(`errors.${error.message}`));
    } else {
      console.log(error);
      toast.error(t("toast.productsLoadError"));
    }
  }, [query.isError, query.error, t]);

  return {
    isLoading: query.isPending || query.isFetching,
    data: query.data ?? null,
    refetch: query.refetch,
  };
}
